import React, { useEffect, useMemo, useState } from 'react';
import { useLocaleContext } from '../../../../context/LocaleContext';
import { useModalContext } from '../../../../context/ModalContext';
import { useEventContext } from '../../../../context/EventContext';
import { useStreamContext } from '../../../../context/StreamContext';
import { useUserContext } from '../../../../context/UserContext';
import { TYPE_PROGRAM } from '../../../../constants/page-types';
import { pageByType } from '../../../../helpers/page-helpers';
import { getMemberEpisodes } from '../../../../helpers/members';
import getFullname from '../../../../helpers/get-fullname';
import getMemberNickname from '../../../../helpers/get-member-nickname';
import { generateDateWithDay, unixDiff } from '../../../../helpers/times';
import MemberPhoto from '../MemberPhoto';
import ScheduleMemberLineCard from '../../../Schedule/ScheduleCards/ScheduleCardOffline/ScheduleMemberLineCard';
import ScheduleDetailsModal from '../../../Schedule/ScheduleDetailsModal';
import { CTags, CSocials } from '../../../UI';
import ButtonQuestionWriteFormWrapper from '../../../QuestionMessages/ButtonQuestionWriteFormWrapper';
import { Message as MessageIcon } from '../../../Icons/IconPackage/Message';

import './style.scss';

const ButtonQuestionWriteForm = ({onClick, title}) => (
    <button
        className={'question-message-write-button'}
        onClick={onClick}
    >
        <MessageIcon />
        <span>{title}</span>
    </button>
);

/**
 * @param {object} event Required param
 * @param {object} member Required param
 * @returns {ReactComponent}
 */

const MemberDetailsModal = ({member, settings = null}) => {
    const $locale = useLocaleContext();
    const {event, pages} = useEventContext();
    const streamContext = useStreamContext();
    const {renderModal} = useModalContext();
    const {hasAuthentication} = useUserContext();
    const [memberEpisodes, setMemberEpisodes] = useState(null);
    const [isMultiDaysEvent, setMultiDaysEvent] = useState(false);

    const {linkDzen, linkTelegram, linkVk} = member;
    const socials = Object.entries({linkDzen, linkTelegram, linkVk}).map(([key, value]) => {
        return {type: key.replace('link', '').toLowerCase(), link: value};
    });

    useEffect(() => {
        setMultiDaysEvent(!!unixDiff(event.timeStart, event.timeStop, 'days'));
        setMemberEpisodes(getMemberEpisodes(event, member.id));
    }, []);

    const episodesDates = useMemo(() => {
        if (!memberEpisodes) {
            return [];
        }

        return [...new Set(memberEpisodes.map(({timeStart}) => generateDateWithDay(timeStart, $locale.name)))];
    }, [memberEpisodes]);

    return (
        <div className="member-details-modal">
            <div className="member-details-modal-info">
                <div className="member-details-modal-info-col">
                    <MemberPhoto
                        photo={member.profileUrl || undefined}
                        initials={getMemberNickname(member, $locale)}
                        size="xxl"
                    />
                    {
                        !!socials.length &&
                        <CSocials socials={socials} />
                    }
                </div>
                <div className="member-details-modal-info-col">
                    <div className="member-details-modal-info-user">
                        <p className="member-details-modal-info-user__fullname">
                            {getFullname(member, $locale)}
                        </p>
                        <CTags
                            tags={member.roles}
                            size="small"
                        />
                        <div>
                            <p className="member-details-modal-info-user__company">
                                {$locale.getProperty(member, 'company')}
                            </p>
                        </div>
                        <p className="member-details-modal-info-user__regalia">
                            {$locale.getProperty(member, 'regalia')}
                        </p>
                    </div>
                    <CTags tags={member.tags} />
                    <p className="member-details-modal-info-user__biography">
                        {$locale.getProperty(member, 'biography')}
                    </p>
                    {(hasAuthentication && member.allowQuestions && event.moduleQuestion.allowTargetMessage) && (
                        <ButtonQuestionWriteFormWrapper
                            settings={settings}
                            member={member}
                            selectStream={null}
                            ButtonComponent={ButtonQuestionWriteForm}
                        />
                    )}
                </div>
            </div>
            {
                (!!memberEpisodes?.length) &&
                <div className="member-details-modal-episodes">
                    <h3 className="member-details-modal-episodes__title">
                        {$locale.get('member.activity')}
                    </h3>
                    {
                        episodesDates.map(date => (
                        <React.Fragment key={date}>
                            {
                                (episodesDates.length > 1 || isMultiDaysEvent) &&
                                <div className="member-details-modal-episodes__date">
                                    {date}
                                </div>
                            }
                            {
                                memberEpisodes.filter(({timeStart}) => generateDateWithDay(timeStart, $locale.name) === date).map((episode) => (
                                    <ScheduleMemberLineCard
                                        key={episode.id}
                                        member={member}
                                        episode={episode}
                                        showHall={streamContext.streams?.length > 1}
                                        onClick={() => {
                                            renderModal(
                                                ScheduleDetailsModal,
                                                {
                                                    lockPageScroll: false,
                                                    episode,
                                                    settings: settings
                                                        ? {
                                                            showEpisodePopupStatus: settings.showMemberPopupStatus,
                                                            showEpisodePopupFormat: settings.showMemberPopupFormat,
                                                            ...settings,
                                                        }
                                                        : pageByType(pages, TYPE_PROGRAM)?.settings
                                                },
                                                null,
                                                (settings?.showEpisodePopup ?? true)
                                            )
                                        }}
                                        settings={settings ?? pageByType(pages, TYPE_PROGRAM)?.settings}
                                    />
                                ))
                            }
                        </React.Fragment>
                        ))
                    }
                </div>
            }
        </div>
    )
};

export default MemberDetailsModal;
